import type { Euler } from 'three';

import {
  hasKeyPoseBoneSelection,
  hasVisibleUserKeyPoseGhostsForReference,
  isIntervalKeyPoseRecord,
  isKeyPoseEditableAtPlaybackFrame,
  isKeyPoseGizmoEditingActive,
} from '../keyPoseSelectors';
import { useStore as useKeyPoseStore } from '../keyPoseStore';
import type { KeyPoseId } from '../types';
import { useCurrentFrameKeyPoseEditing } from './useCurrentFrameKeyPoseEditing';

interface UseCurrentFrameKeyPoseRenderStateInput {
  sceneRotation?: Euler;
}

interface CurrentFrameKeyPoseRenderState {
  editableKeyPoseId: KeyPoseId | null;
  editableReferenceName: string | null;
  liveActorHiddenReferenceName: string | null;
  liveActorReplacementKeyPoseId: KeyPoseId | null;
  showKeyPoseGizmos: boolean;
}

export function useCurrentFrameKeyPoseRenderState({
  sceneRotation,
}: UseCurrentFrameKeyPoseRenderStateInput): CurrentFrameKeyPoseRenderState {
  const keyPoseStore = useKeyPoseStore();
  const {
    actorScene,
    isPlaying,
    keyPose,
    keyPoseAtCurrentPlaybackFrame,
    referenceName,
  } = useCurrentFrameKeyPoseEditing({ sceneRotation });
  const { editing } = keyPoseStore.state;
  const { draggingKeyPoseId } = editing;
  const hasEditingSelection = hasKeyPoseBoneSelection(editing);
  const hasVisibleUserKeyPoseGhosts = referenceName
    ? hasVisibleUserKeyPoseGhostsForReference(
        keyPoseStore.state,
        referenceName,
      )
    : false;
  const liveActorReplacementKeyPose =
    actorScene &&
    keyPoseAtCurrentPlaybackFrame &&
    isKeyPoseEditableAtPlaybackFrame(
      keyPoseAtCurrentPlaybackFrame,
      hasVisibleUserKeyPoseGhosts,
    )
      ? keyPoseAtCurrentPlaybackFrame
      : null;
  const editableKeyPose =
    !isPlaying && liveActorReplacementKeyPose
      ? liveActorReplacementKeyPose
      : null;
  const isDraggingCurrentIntervalKeyPose =
    Boolean(draggingKeyPoseId) &&
    keyPose?.id === draggingKeyPoseId &&
    isIntervalKeyPoseRecord(keyPose);
  const showKeyPoseGizmos =
    !isPlaying &&
    Boolean(actorScene) &&
    (isKeyPoseGizmoEditingActive(
      keyPose,
      editing,
      hasVisibleUserKeyPoseGhosts,
    ) ||
      (Boolean(keyPose) && hasEditingSelection));
  const liveActorHiddenReferenceName =
    actorScene &&
    referenceName &&
    (liveActorReplacementKeyPose ||
      (!isPlaying && isDraggingCurrentIntervalKeyPose))
      ? referenceName
      : null;

  return {
    editableKeyPoseId: editableKeyPose?.id ?? null,
    editableReferenceName: editableKeyPose ? referenceName : null,
    liveActorHiddenReferenceName,
    liveActorReplacementKeyPoseId: liveActorReplacementKeyPose?.id ?? null,
    showKeyPoseGizmos,
  };
}
